import { Box, Button as MuiButton, Link, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { useContext, useEffect, useRef, type JSX } from 'react';
import { MountPointContext } from '@repo/common-utils/mountPoint';
import { useLocale } from '@repo/i18n';
import Modal from 'src/components/common/modal/Modal';
import Button from 'src/components/common/timeslots-buttons/button/Button';
import TextInput from 'src/components/common/timeslots-fields/inputs/text-input/TextInput';
import NativeLoginHeader from './NativeLoginHeader';
import NativePasswordInput from './NativePasswordInput';
import NativeRegisterForm from './NativeRegisterForm';
import { useNativeAuth } from './useNativeAuth';

// Orchestrates the tenant-branded auth modal: header + login/sign-up tabs + the active form
// + a help footer. Form state, submission and tenant branding live in useNativeAuth; this
// component reuses the shared Modal/TextInput/Button so it inherits the tenant's theme.
export default function NativeLoginModal(): JSX.Element {
    const { t } = useLocale();
    const theme = useTheme();
    const mountPoint = useContext(MountPointContext);
    const { mode, setMode, isOpen, close, tenant, subtitle, loginForm, registerForm } =
        useNativeAuth();
    const emailRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (!isOpen || mode !== 'login') return;
        // Defer until the dialog has mounted before moving focus.
        const id = setTimeout(() => emailRef.current?.focus(), 50);
        return () => clearTimeout(id);
    }, [isOpen, mode]);

    return (
        <Modal open={isOpen} onClose={close} shadowRoot={mountPoint.shadowRoot} elevated>
            <Box
                onKeyDown={(e) => {
                    // Enter submits the active form only from an input, so it never
                    // double-fires with buttons.
                    if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
                        e.preventDefault();
                        if (mode === 'login') void loginForm.submit();
                        else void registerForm.submit();
                    }
                }}
                sx={{
                    // The widget shadow root has no CssBaseline, so divs default to content-box;
                    // without this the shared Modal's `width: 100%` + overflow clip would push the
                    // horizontal padding outside the box and hide it.
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.spacing(2),
                    padding: theme.spacing(4),
                    backgroundColor: theme.palette.background.paper,
                }}
            >
                <NativeLoginHeader
                    name={tenant.name}
                    logoUrl={tenant.logoUrl}
                    hasLogo={tenant.hasLogo}
                    subtitle={subtitle}
                />

                <Tabs
                    value={mode}
                    onChange={(_, value) => setMode(value)}
                    variant="fullWidth"
                    sx={{ minHeight: 'auto' }}
                >
                    <Tab value="login" label={t.login} />
                    <Tab value="signup" label={t.register} />
                </Tabs>

                {mode === 'login' ? (
                    <>
                        <TextInput
                            label={t.email}
                            value={loginForm.email}
                            onChange={loginForm.setEmail}
                            autoComplete="email"
                            inputRef={emailRef}
                            required
                        />
                        <NativePasswordInput
                            label={t.password}
                            value={loginForm.password}
                            onChange={loginForm.setPassword}
                            autoComplete="current-password"
                            required
                        />

                        {loginForm.error && (
                            <Typography role="alert" sx={{ color: theme.palette.error.main }}>
                                {loginForm.error}
                            </Typography>
                        )}
                        {loginForm.forgotSent && (
                            <Typography role="status" sx={{ color: theme.palette.text.secondary }}>
                                {t.forgot_password_email_sent}
                            </Typography>
                        )}

                        <Button
                            text={t.login}
                            onClick={() => void loginForm.submit()}
                            disabled={!loginForm.canSubmit}
                        />
                        <MuiButton
                            variant="text"
                            onClick={() => void loginForm.forgotPassword()}
                            sx={{ alignSelf: 'center', textTransform: 'none' }}
                        >
                            {t.forgot_password}
                        </MuiButton>
                    </>
                ) : (
                    <NativeRegisterForm
                        firstName={registerForm.firstName}
                        setFirstName={registerForm.setFirstName}
                        lastName={registerForm.lastName}
                        setLastName={registerForm.setLastName}
                        email={registerForm.email}
                        setEmail={registerForm.setEmail}
                        password={registerForm.password}
                        setPassword={registerForm.setPassword}
                        passwordConfirm={registerForm.passwordConfirm}
                        setPasswordConfirm={registerForm.setPasswordConfirm}
                        phone={registerForm.phone}
                        setPhone={registerForm.setPhone}
                        country={registerForm.country}
                        setCountry={registerForm.setCountry}
                        acceptTerms={registerForm.acceptTerms}
                        setAcceptTerms={registerForm.setAcceptTerms}
                        errors={registerForm.errors}
                        emailExists={registerForm.emailExists}
                        onSwitchToLogin={registerForm.onSwitchToLogin}
                        termsUrl={registerForm.termsUrl}
                        privacyUrl={registerForm.privacyUrl}
                        canSubmit={registerForm.canSubmit}
                        submit={registerForm.submit}
                    />
                )}

                {tenant.supportEmail && (
                    <Box
                        sx={{
                            borderTop: `1px solid ${theme.palette.divider}`,
                            paddingTop: theme.spacing(2),
                            display: 'flex',
                            flexDirection: 'column',
                            gap: theme.spacing(0.5),
                        }}
                    >
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            {t.need_help}
                        </Typography>
                        <Link href={`mailto:${tenant.supportEmail}`} color="primary">
                            {tenant.supportEmail}
                        </Link>
                    </Box>
                )}
            </Box>
        </Modal>
    );
}
